import * as React from 'react';
import { ThemeProvider as NextThemesProvider, useTheme } from 'next-themes';

import {
  APPLIED_COLOR_SCHEME_MESSAGE_TYPE,
  COLOR_SCHEME_MESSAGE_TYPE,
  COLOR_SCHEME_STORAGE_KEY,
  type ColorScheme,
  type ColorSchemeResolution,
  isColorScheme,
  readInitialColorSchemeSource,
} from './color-scheme';

declare global {
  interface Window {
    glomoSetColorScheme?: (value: unknown) => void;
  }
}

interface ColorSchemeBridgeProps {
  onForcedChange?: (value: ColorScheme) => void;
}

const ColorSchemeBridge = ({ onForcedChange }: ColorSchemeBridgeProps) => {
  const { setTheme } = useTheme();

  React.useEffect(() => {
    const applyColorScheme = (value: unknown) => {
      if (!isColorScheme(value)) return;
      if (onForcedChange) onForcedChange(value);
      else setTheme(value);
    };
    // Web SDKs live on the merchant's origin, which the page cannot know, so the sender is pinned to the
    // embedding window instead of an origin.
    const handleMessage = (event: MessageEvent) => {
      if (window.parent === window || event.source !== window.parent) return;
      if (event.data?.type === COLOR_SCHEME_MESSAGE_TYPE) applyColorScheme(event.data.value);
    };

    window.glomoSetColorScheme = applyColorScheme;
    window.addEventListener('message', handleMessage);
    return () => {
      delete window.glomoSetColorScheme;
      window.removeEventListener('message', handleMessage);
    };
  }, [onForcedChange, setTheme]);

  return null;
};

// An opaque embedder (sandboxed iframe, or an Android WebView whose referrer is `android-app://…`) reports
// its origin as the string "null", which postMessage rejects as a target origin.
const toTargetOrigin = (origin: string | undefined) => (origin && origin !== 'null' ? origin : undefined);

const getParentOrigin = () => {
  const ancestor = window.location.ancestorOrigins?.[0];
  if (ancestor) return toTargetOrigin(ancestor);
  try {
    return document.referrer ? toTargetOrigin(new URL(document.referrer).origin) : undefined;
  } catch {
    return undefined;
  }
};

// The init plugin's meta tag carries only the default scheme; keyboards and form controls follow it,
// so it has to track the scheme actually applied, including light while the flag is off.
// An embedding iframe whose color-scheme differs from this root paints an opaque canvas, so the parent is told
// which scheme was applied.
const ColorSchemeMetaSync = () => {
  const { forcedTheme, resolvedTheme } = useTheme();
  const scheme = forcedTheme ?? resolvedTheme;

  React.useEffect(() => {
    if (scheme !== 'light' && scheme !== 'dark') return;
    document.querySelector('meta[name="color-scheme"]')?.setAttribute('content', scheme);
    if (window.parent === window) return;
    const parentOrigin = getParentOrigin();
    if (!parentOrigin) return;
    try {
      window.parent.postMessage({ type: APPLIED_COLOR_SCHEME_MESSAGE_TYPE, value: scheme }, parentOrigin);
    } catch {
      // Best-effort notice to the embedder; a parent that refuses it must not take the app down.
    }
  }, [scheme]);

  return null;
};

const DARK_QUERY = '(prefers-color-scheme: dark)';

const getSystemScheme = () => (window.matchMedia?.(DARK_QUERY).matches ? 'dark' : 'light');

// next-themes skips its own media listener whenever `forcedTheme` is set, so a forced `system` would never follow the OS.
const useSystemScheme = (enabled: boolean) => {
  const [scheme, setScheme] = React.useState(getSystemScheme);

  React.useEffect(() => {
    if (!enabled) return;
    const media = window.matchMedia(DARK_QUERY);
    const onChange = () => setScheme(media.matches ? 'dark' : 'light');
    onChange();
    // Safari < 14 (iOS 13 WKWebView) only has the deprecated addListener.
    if (typeof media.addEventListener !== 'function') {
      media.addListener(onChange);
      return () => media.removeListener(onChange);
    }
    media.addEventListener('change', onChange);
    return () => media.removeEventListener('change', onChange);
  }, [enabled]);

  return scheme;
};

interface ColorSchemeReporterProps {
  enabled: boolean;
  forcedTheme?: ColorScheme;
  isFromMessage: boolean;
  isResolving: boolean;
  onResolved: (resolution: ColorSchemeResolution) => void;
}

// Reports once per resolution, plus each live message, so a later toggle or OS change does not count as a new choice.
// Resolving again (e.g. after logout) re-arms it, so the next session reports its own choice.
const ColorSchemeReporter = ({ enabled, forcedTheme, isFromMessage, isResolving, onResolved }: ColorSchemeReporterProps) => {
  const { theme } = useTheme();
  const lastReportedRef = React.useRef<string | undefined>(undefined);
  const colorScheme = enabled ? (forcedTheme ?? theme) : 'light';
  const colorSchemeSource = !enabled ? 'disabled' : isFromMessage ? 'message' : readInitialColorSchemeSource();

  React.useEffect(() => {
    if (isResolving) {
      lastReportedRef.current = undefined;
      return;
    }
    if (!isColorScheme(colorScheme)) return;
    const key = `${colorScheme}:${colorSchemeSource}`;
    if (lastReportedRef.current === key || (lastReportedRef.current !== undefined && colorSchemeSource !== 'message')) return;
    lastReportedRef.current = key;
    onResolved({ colorScheme, colorSchemeSource });
  }, [colorScheme, colorSchemeSource, isResolving, onResolved]);

  return null;
};

export interface ThemeProviderProps {
  children: React.ReactNode;
  defaultTheme?: ColorScheme;
  forcedTheme?: ColorScheme;
  enabled?: boolean;
  // True while the app still waits on the flag that decides `enabled`, so the interim scheme is not reported.
  isResolving?: boolean;
  onColorSchemeResolved?: (resolution: ColorSchemeResolution) => void;
}

export const ThemeProvider = ({
  children,
  defaultTheme = 'light',
  forcedTheme,
  enabled,
  isResolving = false,
  onColorSchemeResolved,
}: ThemeProviderProps) => {
  const [liveForcedTheme, setLiveForcedTheme] = React.useState<ColorScheme>();
  const isForced = forcedTheme !== undefined;
  // While disabled, setTheme / live requests still land in state (and storage) under the light override,
  // so turning the flag on applies the latest request.
  const activeForcedTheme = isForced ? (liveForcedTheme ?? forcedTheme) : undefined;
  const systemScheme = useSystemScheme(enabled === true && activeForcedTheme === 'system');

  React.useEffect(() => setLiveForcedTheme(undefined), [forcedTheme]);

  return (
    <NextThemesProvider
      attribute='class'
      storageKey={COLOR_SCHEME_STORAGE_KEY}
      themes={['light', 'dark']}
      enableSystem
      defaultTheme={defaultTheme}
      forcedTheme={enabled === true ? (activeForcedTheme === 'system' ? systemScheme : activeForcedTheme) : 'light'}
      disableTransitionOnChange>
      <ColorSchemeBridge onForcedChange={isForced ? setLiveForcedTheme : undefined} />
      <ColorSchemeMetaSync />
      {onColorSchemeResolved && (
        <ColorSchemeReporter
          enabled={enabled === true}
          forcedTheme={activeForcedTheme}
          isFromMessage={liveForcedTheme !== undefined}
          isResolving={isResolving}
          onResolved={onColorSchemeResolved}
        />
      )}
      {children}
    </NextThemesProvider>
  );
};
